import React, { useState, useEffect } from 'react';
import api from '../utils/axiosInstance';
import '@fontsource/oswald';
import { loadStripe } from '@stripe/stripe-js';

// Load Stripe using the environment variable defined for the rest of the application
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const savedRole = localStorage.getItem('userRole');
const SpecialPackageCard = ({ onPurchaseSuccess }) => {
    const [packageData, setPackageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false); //
    const [errorMsg, setErrorMsg] = useState('');
    const [purchasing, setPurchasing] = useState(false); // true while purchase API runs
    const [successMsg, setSuccessMsg] = useState(''); // shown after purchase

    useEffect(() => {
        const fetchPackage = async () => {
            try {
                const res = await api.get('/special-package/details');
                const data = res.data;

                if (data.success) {
                    setPackageData(data.package);
                } else {
                    setErrorMsg('Could not load package details.');
                }
            } catch (err) {
                console.error('Failed to fetch special package:', err);
                setErrorMsg('Network error. Please refresh.');
            } finally {
                setLoading(false);
            }
        };

        fetchPackage();
    }, []);

    // ── Purchase handler — called when "Click to pay" is clicked
    const handlePurchase = async () => {
        if (!packageData) return;

        setErrorMsg('');
        setSuccessMsg('');
        setPurchasing(true);

        try {
            // Call the new backend Stripe checkout endpoint ──
            // Retrieve the logged-in user's carrier ID from localStorage
            const storedUser = JSON.parse(localStorage.getItem('carrierUser') || '{}');
            const carrierId = storedUser?._id;

            // Call the NEW backend endpoint to create a Stripe Checkout Session
            // Instead of telling the backend to instantly buy it, we ask for a Stripe Session ID.
            const res = await api.post('/special-package/create-checkout-session', {
                specialPackageId: packageData._id,
                carrierId: carrierId,
            });
            // Validate the session ID was created
            if (res.data.success && res.data.id) {
                // Wait for the Stripe object to load
                const stripe = await stripePromise;

                // Redirect user to Stripe's secure payment page
                // This completely hands over the user to Stripe.
                const result = await stripe.redirectToCheckout({ sessionId: res.data.id });
                // If something goes wrong with the redirect itself (e.g. browser blocks it)
                if (result.error) {
                    console.error('Stripe Redirect Error:', result.error.message);
                    setErrorMsg('Failed to connect to the payment gateway.');
                }
            } else {
                setErrorMsg(res.data.message || 'Purchase session failed. Please try again.');
            }
        } catch (err) {
            console.error('SpecialPackageCard: purchase error:', err);
            // Show any error text sent from Express
            setErrorMsg(err.response?.data?.message || 'Network error. Please try again.');
        } finally {
            setPurchasing(false);
        }
    };

    if (loading) {
        return (
            <div className='w-[320px] bg-white border border-gray-200 rounded-xl p-6 shadow-sm'>
                <p className='text-gray-500 text-sm'>Loading Special Package...</p>
            </div>
        );
    }

    if (!packageData) return null;

    return (
        <>
            {/* ─────────────────────────────────────────────
               MAIN CARD
            ───────────────────────────────────────────── */}
            <div
                className='
  w-[420px] rounded-2xl p-6
  bg-white/10 backdrop-blur-xl
  border border-white/20
  shadow-[0_8px_30px_rgba(0,0,0,0.15)]
  hover:shadow-[0_10px_40px_rgba(59,130,246,0.25)]
  transition-all duration-300'
            >
                {/* Badge */}
                {/* <div className='inline-block bg-blue-100 text-blue-600 text-xs font-semibold uppercase px-3 py-1 rounded-full mb-3 font-oswald'>
                    ★ Premium Special
                </div> */}

                {/* Title */}
                {/* <h2 className='text-xl font-bold text-gray-800 font-oswald'>{packageData.name}</h2> */}
                <div className='flex items-center justify-between mb-4'>
                    <h3 className='text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-br from-[#d4af37] to-[#92700a] leading-normal font-oswald'>
                        {packageData.name}
                    </h3>
                    {/* text-5xl font-extrabold mb-8 text-transparent bg-clip-text bg-gradient-to-br from-[#d4af37]
                    to-[#92700a] leading-normal */}
                    {/* Price */}
                    <div className='flex items-center justify-betwee'>
                        <span className='text-green-600 font-bold text-lg font-oswald'>${packageData.price}</span>
                        {/* <span className='text-sm text-gray-400 ml-1'>/ {packageData.durationDays} days</span> */}
                    </div>
                </div>

                {/* Description */}
                {/* <p className='text-sm text-gray-500 mt-1 mb-4 font-oswald'>{packageData.description}</p> */}

                {/* Features */}
                <div className='mb-4'>
                    {' '}
                    <p className='text-sm text-gray-700 font-medium'>Features:</p>
                    <ul className='list-disc list-inside mt-1 text-gray-600 text-sm space-y-1'>
                        {Array.isArray(packageData.features) && packageData.features.length > 0 ? (
                            packageData.features.map((feature, index) => <li key={index}>{feature}</li>)
                        ) : (
                            <li>N/A</li>
                        )}
                    </ul>
                </div>

                {/* UPDATED BUTTON */}
                <button
                    onClick={() => {
                        setSuccessMsg(''); // clear any previous success/error messages
                        setErrorMsg('');
                        setShowModal(true);
                    }}
                    className='w-full mt-4 py-2 px-4 rounded-lg text-white font-semibold text-sm shadow transition bg-gradient-to-br from-[#d4af37] to-[#92700a]'
                >
                    View Package
                </button>
            </div>

            {/* ─────────────────────────────────────────────
               MODAL POPUP
            ───────────────────────────────────────────── */}

            {showModal && (
                <div className='fixed inset-0 flex items-center justify-center px-4 py-6 z-50 bg-black/40'>
                    <div className='relative w-full max-w-[1200px] bg-white rounded-3xl shadow-2xl overflow-hidden min-h-[600px] flex flex-col md:flex-row'>
                        <button
                            onClick={() => setShowModal(false)}
                            className='absolute top-4 right-4 text-white text-4xl font-extrabold z-10 p-2 rounded-full hover:bg-white/20 transition'
                            aria-label='Close modal'
                        >
                            &times;
                        </button>

                        <div className='w-full md:w-2/3 p-10 bg-white'>
                            <h2 className='text-5xl font-extrabold mb-8 text-transparent bg-clip-text bg-gradient-to-br from-[#d4af37] to-[#92700a] leading-normal font-oswald'>
                                {packageData.name}
                            </h2>

                            <h3 className='text-2xl font-semibold mb-6 text-gray-800'>Package Benefits</h3>

                            <ul className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                {Array.isArray(packageData.features) && packageData.features.length > 0 ? (
                                    packageData.features.map((f, i) => (
                                        <li key={i} className='flex items-start text-gray-800 text-base'>
                                            <svg
                                                className='w-6 h-6 text-blue-600 mr-3 mt-1 shrink-0'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                            >
                                                <path
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                    strokeWidth='2'
                                                    d='M5 13l4 4L19 7'
                                                />
                                            </svg>
                                            {f}
                                        </li>
                                    ))
                                ) : (
                                    <li className='text-gray-600'>N/A</li>
                                )}
                            </ul>

                            <div className='mt-4 p-10 bg-gray-800 rounded-lg text-black'>
                                <p className='text-md text-gray-300'>{packageData.description}</p>
                            </div>
                        </div>

                        <div className='w-full md:w-1/3 bg-gradient-to-br from-[#d4af37] to-[#92700a] text-white p-10 flex flex-col justify-between'>
                            <div>
                                {/* Price */}
                                <div className='mb-8'>
                                    <p className='text-sm text-gray-300'>Price</p>
                                    <p className='text-5xl font-bold'>${packageData.price}</p>
                                </div>

                                {/* Duration — uses "duration" field from SpecialPackage model */}
                                <div className='mb-6'>
                                    <p className='text-sm text-gray-300'>Duration</p>
                                    <p className='text-lg'>{packageData.durationDays} Days</p>
                                </div>

                                {/* Access Details — how many logins are included */}
                                <div className='mb-6'>
                                    <p className='text-sm text-gray-300'>Access Details</p>
                                    <p className='text-lg'>{packageData.accessDetails} Login</p>
                                </div>

                                {/* Package code */}
                                <div className='mb-6'>
                                    <p className='text-sm text-gray-300'>Code</p>
                                    <p className='text-lg'>{packageData.code}</p>
                                </div>

                                {/* ── NEW: note box — explains this works alongside subscription ── */}
                                <div className='bg-white/10 border border-white/20 rounded-lg p-3 text-xs text-gray-300 leading-relaxed'>
                                    This package can be purchased even if you already have an active subscription. Both
                                    run independently.
                                </div>
                            </div>
                            {/* ── Purchase button + success/error messages ─────────── */}
                            <div className='mt-8'>
                                {/* Hide payment section for admin roles */}
                                {!['SUPER_ADMIN', 'MAIN_ADMIN', 'MARKETING_ADMIN', 'ACCOUNTS_ADMIN'].includes(
                                    savedRole,
                                ) && (
                                    <>
                                        {successMsg ? (
                                            <div className='bg-green-500/20 border border-green-400/40 rounded-lg p-4 text-green-300 text-sm text-center mb-4'>
                                                🎉 {successMsg}
                                            </div>
                                        ) : (
                                            <>
                                                {errorMsg && (
                                                    <div className='bg-red-500/20 border border-red-400/40 rounded-lg p-3 text-red-300 text-xs text-center mb-3'>
                                                        {errorMsg}
                                                    </div>
                                                )}
                                                <div className='flex justify-center'>
                                                    <button
                                                        onClick={handlePurchase}
                                                        disabled={purchasing}
                                                        className={`
                                bg-white text-black rounded-lg h-12 px-8
                                shadow hover:shadow-md transition
                                flex items-center justify-center
                                font-semibold text-base
                                ${purchasing ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-100'}
                            `}
                                                    >
                                                        {purchasing ? 'Processing...' : 'Click To Pay'}
                                                    </button>
                                                </div>
                                                <div style={{ textAlign: 'center' }} className='mt-2'>
                                                    <span
                                                        style={{
                                                            display: 'block',
                                                            fontSize: '0.90em',
                                                            color: '#fff',
                                                            letterSpacing: '0.5px',
                                                        }}
                                                    >
                                                        Powered by{' '}
                                                        <span style={{ fontWeight: 600, letterSpacing: '1px' }}>
                                                            STRIPE
                                                        </span>
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SpecialPackageCard;
